import RfqStepBadge from '@/dealers/components/RfqStepBadge'
import A from '@/gf/components/A'
import Layout from '@/gf/components/Layout'
import NeededByPill from '@/gf/components/NeededByPill'
import OfficeBuildingTwo from '@/gf/components/OfficeBuildingTwo'
import Page from '@/gf/components/Page'
import Spinner from '@/gf/components/Spinner'
import Tabs from '@/gf/components/Tabs'
import useConfig from '@/gf/hooks/useConfig'
import ConversationM from '@/gf/modules/Conversation'
import FilterSet from '@/gf/modules/FilterSet'
import FormatFilters from '@/gf/modules/FormatFilters'
import RequestForQuoteM from '@/gf/modules/RequestForQuote'
import UserM from '@/gf/modules/User'
import EqualsFilter from '@/gf/modules/filters/Equals'
import {
  FetchConversationsQueryResult,
  RequestForQuoteQuery,
  RequestForQuoteStep,
  useFetchConversationsQuery,
  useRequestForQuoteQuery,
} from '@/suppliers/_gen/gql'
import { ApolloError } from '@apollo/client'
import { Cog6ToothIcon } from '@heroicons/react/24/outline'
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { titleCase } from 'title-case'
import { NumberParam, useQueryParam, withDefault } from 'use-query-params'
import { RequestForQuote as RequestForQuoteT, ShippingOptionDisplay, Tab, User } from '../../types'
import AdminRequestForQuoteInbox from '../components/AdminRequestForQuoteInbox'
import BrokerWarning from '../components/BrokerWarning'
import Frame from '../components/Frame'
import RequestForQuoteInbox from '../components/RequestForQuoteInbox'
import useGqlClient from '../hooks/useGqlClient'
import useSession from '../hooks/useSession'
import { SessionStore } from '../types'
import Details from './RequestForQuote/Details'

const Messages = ({
  rfq,
  store,
  conversationsData,
  conversationsError,
  conversationsRefetch,
  user,
  page,
  setPage,
}: {
  rfq: Exclude<RequestForQuoteQuery['requestForQuote'], null>
  store: SessionStore
  conversationsData: NonNullable<FetchConversationsQueryResult>['data']
  conversationsError: ApolloError | undefined
  conversationsRefetch: () => Promise<unknown>
  user: Pick<User, 'id' | 'role'>
  page: number
  setPage: (page: number) => void
}) => {
  const { conversationId } = useParams()
  const navigate = useNavigate()
  const onSelectedConversationIdChange = (selectedConversationId: string | undefined) =>
    selectedConversationId
      ? navigate(`/rfqs/${rfq.id}/messages/${selectedConversationId}`)
      : navigate(`/rfqs/${rfq.id}/messages`)
  return (
    <div className="-mt-2 flex grow">
      {conversationsError ? (
        <Layout.FullPageLayout>
          <p className="text-sm text-gray-700">{conversationsError.message}</p>
        </Layout.FullPageLayout>
      ) : !conversationsData ? (
        <Layout.FullPageLayout>
          <Spinner />
        </Layout.FullPageLayout>
      ) : UserM.isAdmin(user) ? (
        <AdminRequestForQuoteInbox
          conversations={conversationsData.fetchConversations.conversations}
          refetchConversations={conversationsRefetch}
          page={page}
          setPage={setPage}
          pagination={conversationsData.fetchConversations.pagination}
          user={user}
          requestForQuote={rfq as unknown as RequestForQuoteT & { step: RequestForQuoteStep }}
          selectedConversationId={conversationId}
          onSelectedConversationIdChange={onSelectedConversationIdChange}
        />
      ) : (
        <RequestForQuoteInbox
          conversations={conversationsData.fetchConversations.conversations}
          refetchConversations={conversationsRefetch}
          page={page}
          setPage={setPage}
          pagination={conversationsData.fetchConversations.pagination}
          user={user}
          requestForQuote={rfq as unknown as RequestForQuoteT & { step: RequestForQuoteStep }}
          store={store}
          selectedConversationId={conversationId}
          onSelectedConversationIdChange={onSelectedConversationIdChange}
        />
      )}
    </div>
  )
}

const useQueryParamOpts = withDefault(NumberParam, 1)

const RequestForQuote = () => {
  const { requestForQuoteId } = useParams<{ requestForQuoteId: string }>() as {
    requestForQuoteId: string
  }
  const navigate = useNavigate()
  const config = useConfig()
  const { user, store } = useSession()
  const { pathname } = useLocation()
  const [searchParams, _setSearchParams] = useSearchParams()
  const copyStoreOrderId = searchParams.get('copyStoreOrderId') ?? undefined
  const gqlClient = useGqlClient()

  const {
    data,
    loading: loadingRfq,
    error: errorRfq,
    refetch: refetchRfq,
  } = useRequestForQuoteQuery({
    variables: { id: requestForQuoteId, vendorId: null, vendorUserId: null },
    fetchPolicy: 'no-cache',
    client: gqlClient,
  })

  const rfq = data?.requestForQuote

  const [page, setPage] = useQueryParam('page', useQueryParamOpts)
  const conversationFilters = [
    EqualsFilter.build({ fieldId: 'requestForQuoteId', text: requestForQuoteId }),
  ]
  const {
    data: conversationsData,
    error: conversationsError,
    refetch: conversationsRefetch,
  } = useFetchConversationsQuery({
    client: gqlClient,
    variables: {
      filters: FormatFilters.andFilters(FilterSet.toApiFilters(conversationFilters)),
      page: 1,
      pageSize: ConversationM.PAGE_SIZE,
      vendorId: null,
      vendorUserId: null,
    },
    pollInterval: ConversationM.CONVERSATION_POLL_INTERVAL,
  })

  const tabNameDetails = 'Details'
  const tabNameMessages = 'Messages'
  const tabs: Tab[] = [
    { name: tabNameDetails },
    {
      name: tabNameMessages,
      showNotification: conversationsData?.fetchConversations.conversations.some(
        (c) => c.unreadMessages
      ),
    },
  ]

  const breadcrumbs = {
    copy: 'Back to Dashboard',
    crumbs: [
      { name: 'RFQs', href: '/rfqs' },
      { name: 'RFQ details', href: `/rfqs/${requestForQuoteId}` },
    ],
  }

  const isUrgent = rfq?.shippingOption
    ? [ShippingOptionDisplay.Expedited, ShippingOptionDisplay.Overnight].includes(
        titleCase(rfq.shippingOption) as ShippingOptionDisplay
      )
    : false

  return (
    <Frame breadcrumbs={breadcrumbs}>
      {errorRfq ? (
        <Page title={`RFQ ${RequestForQuoteM.shortenId(requestForQuoteId)}`}>
          <Layout>
            <Layout.Section type="full">Error fetching RFQ, please contact support</Layout.Section>
          </Layout>
        </Page>
      ) : loadingRfq || !rfq ? (
        <Page title={`RFQ ${RequestForQuoteM.shortenId(requestForQuoteId)}`}>
          <Layout>
            <Layout.Section type="full">
              <Spinner className="mt-4" />
            </Layout.Section>
          </Layout>
        </Page>
      ) : (
        <Page
          title={`RFQ ${rfq.shortId}`}
          titleMetadata={
            <div className="flex grow flex-row items-center gap-x-4">
              <RfqStepBadge step={rfq.step} />
              {rfq.brokeredByRequestForQuotes && rfq.brokeredByRequestForQuotes.length > 0 ? (
                <BrokerWarning.Request
                  className="-my-2"
                  brokerRfqIds={rfq.brokeredByRequestForQuotes.map(({ id }) => id)}
                />
              ) : rfq.broadcast && store.isBrokerEnabled ? (
                <BrokerWarning className="-my-2">
                  <A.T href={`${config.buyersUrl}/rfqs/${rfq.id}/broker`} target="_blank">
                    Create Broker Request
                  </A.T>
                </BrokerWarning>
              ) : null}
            </div>
          }
          yellowAlert={
            isUrgent
              ? `The customer indicated that this RFQ is urgent and is seeking ${rfq.shippingOption} shipping`
              : undefined
          }
          titleExtra={
            UserM.isAdmin(user) ? (
              rfq?.broadcast ? (
                <Cog6ToothIcon className="h-7 w-7 text-gearflow" />
              ) : (
                <OfficeBuildingTwo className="h-7 w-7 text-blue-500" />
              )
            ) : undefined
          }
          titleRightContent={<NeededByPill rfq={rfq} />}
        >
          <Tabs
            className="mt-4"
            currentTabName={pathname.includes('/messages') ? tabNameMessages : tabNameDetails}
            onTabSelect={(tabName) => {
              if (tabName === tabNameDetails) navigate(`/rfqs/${requestForQuoteId}`)
              if (tabName === tabNameMessages) navigate(`/rfqs/${requestForQuoteId}/messages`)
            }}
            tabs={tabs}
          />
          <Routes>
            <Route
              path=""
              element={
                <div className="-mt-2">
                  <Details
                    rfq={rfq}
                    refetchRfq={refetchRfq}
                    store={store}
                    user={user}
                    openMessagesTab={() => navigate(`/rfqs/${requestForQuoteId}/messages`)}
                    copyStoreOrderId={copyStoreOrderId}
                  />
                </div>
              }
            />
            {['messages', 'messages/:conversationId'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <Messages
                    rfq={rfq}
                    store={store}
                    conversationsData={conversationsData}
                    conversationsError={conversationsError}
                    conversationsRefetch={conversationsRefetch}
                    user={user}
                    page={page}
                    setPage={setPage}
                  />
                }
              />
            ))}
          </Routes>
        </Page>
      )}
    </Frame>
  )
}

export default RequestForQuote
